import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const familyMembers = [
  // Immediate Family
  { 
    word: "أُمّ", 
    pronunciation: "umm", 
    meaning: "Mother",
    emoji: "👩",
    category: "Immediate Family"
  },
  { 
    word: "أَب", 
    pronunciation: "ab", 
    meaning: "Father",
    emoji: "👨",
    category: "Immediate Family"
  },
  { 
    word: "أَخ", 
    pronunciation: "akh", 
    meaning: "Brother",
    emoji: "👦",
    category: "Immediate Family"
  },
  { 
    word: "أُخْت", 
    pronunciation: "ukht", 
    meaning: "Sister",
    emoji: "👧",
    category: "Immediate Family"
  },
  { 
    word: "زَوْج", 
    pronunciation: "zawj", 
    meaning: "Husband",
    emoji: "💑",
    category: "Immediate Family"
  },
  { 
    word: "زَوْجَة", 
    pronunciation: "zawjah", 
    meaning: "Wife",
    emoji: "💑",
    category: "Immediate Family"
  },
  { 
    word: "اِبْن", 
    pronunciation: "ibn", 
    meaning: "Son",
    emoji: "👶",
    category: "Immediate Family"
  },
  { 
    word: "بِنْت", 
    pronunciation: "bint", 
    meaning: "Daughter",
    emoji: "👶",
    category: "Immediate Family"
  },
  // Extended Family
  { 
    word: "جَدّ", 
    pronunciation: "jadd", 
    meaning: "Grandfather",
    emoji: "👴",
    category: "Extended Family"
  },
  { 
    word: "جَدَّة", 
    pronunciation: "jaddah", 
    meaning: "Grandmother",
    emoji: "👵",
    category: "Extended Family"
  },
  { 
    word: "عَمّ", 
    pronunciation: "amm", 
    meaning: "Uncle (Father's brother)",
    emoji: "👨",
    category: "Extended Family"
  },
  { 
    word: "خَال", 
    pronunciation: "khal", 
    meaning: "Uncle (Mother's brother)",
    emoji: "👨",
    category: "Extended Family"
  },
  { 
    word: "عَمَّة", 
    pronunciation: "ammah", 
    meaning: "Aunt (Father's sister)",
    emoji: "👩",
    category: "Extended Family"
  },
  { 
    word: "خَالَة", 
    pronunciation: "khalah", 
    meaning: "Aunt (Mother's sister)",
    emoji: "👩",
    category: "Extended Family"
  }
];

function FamilyLesson() {
  const [selectedMember, setSelectedMember] = useState(null);
  const [clickedMembers, setClickedMembers] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState("All");

  const handleClick = (memberObj) => {
    setSelectedMember(memberObj);
    setClickedMembers((prev) => new Set(prev).add(memberObj.word));
    window.responsiveVoice.speak(memberObj.word, "Arabic Male");
  };

  const filteredMembers = selectedCategory === "All" 
    ? familyMembers 
    : familyMembers.filter(member => member.category === selectedCategory);

  const progress = (clickedMembers.size / familyMembers.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic min-h-screen bg-gradient-to-br from-green-50 to-white">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-green-800">أفراد العائلة</h2>

      {/* Category Filter */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => setSelectedCategory("All")}
          className={`px-4 py-2 rounded-full ${
            selectedCategory === "All" 
              ? "bg-green-500 text-white" 
              : "bg-white text-green-700 hover:bg-green-100"
          }`}
        >
          الكل
        </button>
        <button
          onClick={() => setSelectedCategory("Immediate Family")}
          className={`px-4 py-2 rounded-full ${
            selectedCategory === "Immediate Family" 
              ? "bg-green-500 text-white" 
              : "bg-white text-green-700 hover:bg-green-100"
          }`}
        >
          العائلة المباشرة
        </button>
        <button
          onClick={() => setSelectedCategory("Extended Family")}
          className={`px-4 py-2 rounded-full ${
            selectedCategory === "Extended Family" 
              ? "bg-green-500 text-white" 
              : "bg-white text-green-700 hover:bg-green-100"
          }`}
        >
          العائلة الممتدة
        </button>
      </div>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-green-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedMembers.size} من {familyMembers.length} كلمة</p>

      <AnimatePresence>
        {selectedMember && (
          <motion.div
            key="member-info"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-lg sm:text-xl text-green-700 mb-6"
          >
            <p>النطق: <strong>{selectedMember.pronunciation}</strong></p>
            <p>المعنى: <strong>{selectedMember.meaning}</strong></p>
            <p>الفئة: <strong>{selectedMember.category}</strong></p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
        {filteredMembers.map((memberObj, index) => {
          const learned = clickedMembers.has(memberObj.word);
          return (
            <button
              key={index}
              onClick={() => handleClick(memberObj)}
              className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                learned ? "bg-green-100 text-green-700" : "bg-white text-green-800 hover:bg-green-100"
              }`}
            >
              <div className="text-3xl mb-2">{memberObj.emoji}</div>
              {memberObj.word}
            </button>
          );
        })}
      </div>

      {/* Cultural Information */}
      <div className="max-w-2xl mx-auto mt-8 p-4 bg-white rounded-xl shadow-md">
        <h3 className="text-xl font-bold text-green-800 mb-4">معلومات ثقافية</h3>
        <ul className="text-right text-gray-700 space-y-2">
          <li>• في الثقافة العربية، العائلة الممتدة مهمة جداً وتلعب دوراً كبيراً في حياة الفرد</li>
          <li>• هناك مصطلحات مختلفة للأعمام والخالات حسب جانب الأب أو الأم</li>
          <li>• كلمة "عائلة" تعني "family" في اللغة العربية</li>
          <li>• في بعض الدول العربية، يستخدم الناس مصطلحات محلية مختلفة</li>
        </ul>
      </div>

      {/* Common Phrases */}
      <div className="max-w-2xl mx-auto mt-4 p-4 bg-white rounded-xl shadow-md">
        <h3 className="text-xl font-bold text-green-800 mb-4">عبارات شائعة</h3>
        <ul className="text-right text-gray-700 space-y-2">
          <li>• كيف حال عائلتك؟ - How is your family?</li>
          <li>• أهلاً وسهلاً - Welcome (used when greeting family)</li>
          <li>• عائلتي بخير - My family is well</li>
        </ul>
      </div>

      {/* ResponsiveVoice Script */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default FamilyLesson; 